import { EventTrigger } from "./EventTrigger.js";
import { calendarDate, calendarWeekdayShort, groupSegmentsByDay, isSameDay, layoutLanes, splitIntoDaySegments } from "./utils/date-grid.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
//#region src/components/Calendar/WeekGrid.tsx
var HOUR_PX = 40;
var HOURS = Array.from({ length: 24 }, (_, h) => h);
var GRID_COLUMNS = "48px repeat(7, minmax(0, 1fr))";
var hourLabel = (h) => h === 0 ? "12 AM" : h === 12 ? "12 PM" : h < 12 ? `${h} AM` : `${h - 12} PM`;
function WeekGrid(props) {
	const scrollRef = useRef(null);
	const scrolledKeyRef = useRef(null);
	const segmentsByDay = useMemo(() => groupSegmentsByDay(layoutLanes(props.events.flatMap((event) => splitIntoDaySegments(event, props.days, props.timeZone)))), [
		props.days,
		props.events,
		props.timeZone
	]);
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		let earliest = 480;
		for (const list of segmentsByDay.values()) for (const seg of list) earliest = Math.min(earliest, seg.startMin);
		const key = `${props.days[0]}:${earliest}`;
		if (scrolledKeyRef.current === key) return;
		scrolledKeyRef.current = key;
		el.scrollTop = Math.max(0, earliest / 60 * HOUR_PX - 8);
	}, [props.days, segmentsByDay]);
	const [now] = useState(() => Date.now());
	return /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsxs("div", {
		className: "grid",
		style: { gridTemplateColumns: GRID_COLUMNS },
		children: [/* @__PURE__ */ jsx("div", {}), props.days.map((day) => {
			const isToday = isSameDay(day, now, props.timeZone);
			const label = `${calendarWeekdayShort(day, props.timeZone)} ${calendarDate(day, props.timeZone)}`;
			const labelClassName = isToday ? "text-color-strong border-main border-b-2 font-semibold" : "text-color-subtle";
			return /* @__PURE__ */ jsx("div", {
				className: "pb-xxs text-center text-sm",
				children: props.onDayClick ? /* @__PURE__ */ jsx("button", {
					className: classnames(labelClassName, "cursor-pointer"),
					onClick: () => props.onDayClick?.(day),
					type: "button",
					children: label
				}) : /* @__PURE__ */ jsx("span", {
					className: labelClassName,
					children: label
				})
			}, day);
		})]
	}), /* @__PURE__ */ jsx("div", {
		className: "border-border max-h-[560px] overflow-y-auto rounded border",
		ref: scrollRef,
		children: /* @__PURE__ */ jsxs("div", {
			className: "grid",
			style: {
				gridTemplateColumns: GRID_COLUMNS,
				height: 24 * HOUR_PX
			},
			children: [/* @__PURE__ */ jsx("div", {
				className: "relative",
				children: HOURS.map((h) => h > 0 && /* @__PURE__ */ jsx("div", {
					className: "text-color-subtle absolute right-xxs -translate-y-1/2 text-sm",
					style: { top: h * HOUR_PX },
					children: hourLabel(h)
				}, h))
			}), props.days.map((day) => /* @__PURE__ */ jsx(DayColumn, {
				isToday: isSameDay(day, now, props.timeZone),
				onEventClick: props.onEventClick,
				renderEventContent: props.renderEventContent,
				renderEventDetail: props.renderEventDetail,
				segments: segmentsByDay.get(day) ?? []
			}, day))]
		})
	})] });
}
function DayColumn(props) {
	return /* @__PURE__ */ jsxs("div", {
		className: `border-border relative border-l ${props.isToday ? "bg-surface-subtle" : ""}`,
		children: [HOURS.map((h) => h > 0 && /* @__PURE__ */ jsx("div", {
			className: "border-border absolute inset-x-0 border-t",
			style: { top: h * HOUR_PX }
		}, h)), props.segments.map((seg) => {
			return /* @__PURE__ */ jsx("div", {
				className: "absolute px-px",
				style: {
					height: (seg.endMin - seg.startMin) / 60 * HOUR_PX,
					left: `${seg.lane / seg.laneCount * 100}%`,
					top: seg.startMin / 60 * HOUR_PX,
					width: `${1 / seg.laneCount * 100}%`
				},
				children: /* @__PURE__ */ jsx(EventTrigger, {
					className: "block h-full w-full overflow-hidden rounded border text-left text-sm",
					dropdownPlace: "bottom-left",
					event: seg.event,
					onEventClick: props.onEventClick,
					renderEventContent: props.renderEventContent,
					renderEventDetail: props.renderEventDetail,
					segment: {
						clippedEnd: seg.clippedEnd,
						clippedStart: seg.clippedStart
					}
				})
			}, `${seg.event.key}:${seg.dayStartMs}`);
		})]
	});
}
//#endregion
export { WeekGrid };

//# sourceMappingURL=WeekGrid.js.map